import { useEffect, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid2,
  IconButton,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, Hub } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Asn, AsnFormErrors, AsnPayload, emptyAsn } from "../types";
import { useModalStyles } from "../../components/modalStyles";
import { validateAsn } from "../validation";

interface ModalAsnProps {
  open: boolean;
  loading?: boolean;
  asnData?: Asn | null;
  onClose: () => void;
  onSave: (payload: AsnPayload, id?: number) => void;
}

export default function ModalAsn(props: ModalAsnProps) {
  const { open, loading = false, asnData, onClose, onSave } = props;

  const [form, setForm] = useState<AsnPayload>(emptyAsn);
  const [errors, setErrors] = useState<AsnFormErrors>({});

  const theme = useTheme();
  const styles = useModalStyles(theme);
  const isEdit = Boolean(asnData?.id);

  useEffect(() => {
    if (!open) return;

    let initialData: AsnPayload = emptyAsn;

    if (asnData) {
      const { id, ...rest } = asnData;
      initialData = { ...emptyAsn, ...rest };
    }

    setForm(initialData);
    setErrors(validateAsn(initialData));
  }, [open, asnData]);

  const handleChange = (field: keyof AsnPayload, value: string) => {
    const next = { ...form, [field]: value };
    setForm(next);
    setErrors(validateAsn(next));
  };

  const hasErrors = Object.keys(errors).length > 0;

  const handleSave = () => {
    const errs = validateAsn(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    onSave(
      {
        nome_asn: form.nome_asn.trim(),
        asn: form.asn.trim(),
      },
      asnData?.id,
    );
  };

  const fields: {
    field: keyof AsnPayload;
    label: string;
    placeholder: string;
    size: { xs: number; sm: number };
  }[] = [
    {
      field: "nome_asn",
      label: "Nome",
      placeholder: "Digite o nome do ASN",
      size: { xs: 12, sm: 7 },
    },
    {
      field: "asn",
      label: "ASN",
      placeholder: "Digite o número do ASN",
      size: { xs: 12, sm: 5 },
    },
  ];

  return (
    <Modal open={open} onClose={loading ? () => {} : onClose}>
      <Paper sx={{ ...styles.paper, minHeight: "fit-content" }}>
        <Stack sx={styles.boxHeader}>
          <Stack sx={styles.boxTitle}>
            <Stack
              sx={{
                p: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 1.5,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Hub color="primary" />
            </Stack>
            <Stack>
              <Typography
                fontWeight="bold"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "1.05rem",
                  lineHeight: 1.2,
                }}
              >
                {isEdit ? "Editar ASN" : "Adicionar ASN"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {form.nome_asn || "Novo ASN"}
              </Typography>
            </Stack>
          </Stack>
          <IconButton aria-label="close" onClick={onClose} disabled={loading}>
            <Close />
          </IconButton>
        </Stack>

        <Divider sx={styles.divider} />

        <Grid2 container columnSpacing={1} sx={styles.boxFields}>
          {fields.map(({ field, label, placeholder, size }) => (
            <Grid2 size={size} key={field}>
              <Typography variant="subtitle2">{label}</Typography>
              <TextField
                fullWidth
                size="small"
                autoComplete="off"
                placeholder={placeholder}
                value={form[field] ?? ""}
                onChange={(event) => handleChange(field, event.target.value)}
                error={Boolean(errors[field])}
                helperText={errors[field] ?? " "}
              />
            </Grid2>
          ))}
        </Grid2>

        <Box sx={styles.boxActions}>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={hasErrors || loading}
            onClick={handleSave}
          >
            {isEdit ? "Salvar" : "Adicionar"}
          </LoadingButton>
        </Box>
      </Paper>
    </Modal>
  );
}
